<script lang="ts">
  import ProgressStepsContent from "@kontextwerk/web-sdk/ui/navigation/ProgressStepsContent.svelte"
  import { onDestroy, onMount } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import ProgressSteps, {
    type ProgressStep,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import type { ProgressStepContext } from "@kontextwerk/web-sdk/core/navigation/progressSteps"
  import {
    firstCurrentWorkflowStep,
    LOCKED_WORKFLOW_STEP_MESSAGE,
    selectedWorkflowStepIndex,
    workflowStateForStep,
  } from "../../../../../../../../../../_shared/_logic/workflow-progress"
  import type {
    ConfirmAppointmentsInit,
    ConfirmAppointmentsOutputs,
    ConfirmAppointmentsStepId,
    ConfirmAppointmentsWorkflowEvent,
    OverlayHostProps,
  } from "./_shared/_logic/confirm-appointments-contract"
  import {
    applyConfirmAppointmentsWorkflowEvent,
    nextConfirmAppointmentsStep,
  } from "./_logic/transitions"
  import ConfirmEventsStep from "./steps/a_confirm_events"
  import SendConfirmationMailStep from "./steps/b_send_confirmation_mail"
  import SummaryStep from "./steps/c_summary"
  import type { WorkOrderRow } from "../../../../../../../../_shared/_logic/work-order-table-model"

  let {
    organizationId,
    events = [],
    workOrders = [],
    sourceRows = [],
    properties = [],
    customers = [],
    contactPoints = [],
    onCompleted = async () => {},
    onremoveOverlay = () => {},
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: {
    organizationId: string
    events?: ScheduleEvent[]
    workOrders?: WorkOrder[]
    sourceRows?: WorkOrderRow[]
    properties?: Property[]
    customers?: Customer[]
    contactPoints?: ContactPoint[]
    onCompleted?: (events: ScheduleEvent[]) => void | Promise<void>
  } & OverlayHostProps = $props()

  const init: ConfirmAppointmentsInit = $derived({
    organizationId,
    events,
    workOrders,
    sourceRows,
    properties,
    customers,
    contactPoints,
  })

  const stepOrder = ["confirm", "mail", "summary"] as const

  let selectedStep = $state<ConfirmAppointmentsStepId>("confirm")
  let outputs = $state<ConfirmAppointmentsOutputs>({})
  let stepError = $state<string | null>(null)
  let finishing = $state(false)
  let mutatedSinceOpen = false
  let completedInvalidation = false
  let closeInvalidationPromise: Promise<void> | null = null

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(null)

    return () => setActionButtonSnippet?.(null)
  })

  onDestroy(() => {
    if (mutatedSinceOpen && !completedInvalidation) {
      void invalidateAfterClose().catch((err) => {
        console.error("confirm_appointments_close_invalidation_failed", err)
      })
    }
  })

  const setStep = (step: ConfirmAppointmentsStepId) => {
    stepError = null
    selectedStep = step
  }

  const setStepError = (message: string | null) => {
    stepError = message
  }

  const confirmedEvents = () =>
    (outputs.confirm?.items ?? [])
      .map((item) => item.event)
      .filter((event): event is ScheduleEvent => Boolean(event?.id))

  const hasMutated = (event: ConfirmAppointmentsWorkflowEvent): boolean => {
    if (event.type === "confirm.completed") {
      return event.output.items.some((item) => item.status === "confirmed")
    }

    if (event.type === "mail.completed") {
      return (
        !event.output.skippedByUser && event.output.items.some((item) => item.status === "queued")
      )
    }

    return false
  }

  const markMutated = () => {
    mutatedSinceOpen = true
    completedInvalidation = false
    closeInvalidationPromise = null
  }

  const completeParentInvalidation = async () => {
    await onCompleted(confirmedEvents())
    completedInvalidation = true
  }

  const invalidateAfterClose = async () => {
    if (!mutatedSinceOpen || completedInvalidation) return

    if (!closeInvalidationPromise) {
      closeInvalidationPromise = completeParentInvalidation().catch((err) => {
        closeInvalidationPromise = null
        throw err
      })
    }

    await closeInvalidationPromise
  }

  async function applyWorkflowEvent(event: ConfirmAppointmentsWorkflowEvent) {
    if (event.type === "summary.finished") {
      if (finishing) return

      finishing = true
      try {
        await completeParentInvalidation()
        await onremoveOverlay?.()
      } finally {
        finishing = false
      }

      return
    }

    if (hasMutated(event)) {
      markMutated()
    }

    outputs = applyConfirmAppointmentsWorkflowEvent(outputs, event)
    setStep(nextConfirmAppointmentsStep(currentStep ?? selectedStep, event))

    if (event.type === "confirm.completed") {
      const confirmedCount = event.output.items.filter((item) => item.status === "confirmed").length
      const alreadyCount = event.output.items.filter(
        (item) => item.status === "already_confirmed",
      ).length
      const skippedCount = event.output.items.filter((item) => item.status === "skipped").length
      newNotification({
        class: skippedCount ? "warning" : "success",
        html: `${confirmedCount} Termine bestätigt.${alreadyCount ? ` ${alreadyCount} waren bereits bestätigt.` : ""}`,
      })
    }

    if (event.type === "mail.completed" && !event.output.skippedByUser) {
      const queuedCount = event.output.items.filter((item) => item.status === "queued").length
      const skippedCount = event.output.items.filter((item) => item.status === "skipped").length
      newNotification({
        class: skippedCount ? "warning" : "success",
        html: `${queuedCount} Bestätigungsmails vorbereitet.`,
      })
    }
  }

  const completedSteps = $derived.by(() => {
    const completed: ConfirmAppointmentsStepId[] = []

    if (outputs.confirm) completed.push("confirm")

    if (outputs.mail) completed.push("mail")

    if (outputs.summary) completed.push("summary")

    return completed
  })
  const currentStep = $derived(
    firstCurrentWorkflowStep<ConfirmAppointmentsStepId>({
      stepOrder,
      completedSteps,
      selectedStep,
    }),
  )
  const selectedIndex = $derived(
    selectedWorkflowStepIndex<ConfirmAppointmentsStepId>({
      stepOrder,
      completedSteps,
      selectedStep,
    }),
  )
  const handleSelectedIndexChange = (index: number) => {
    const next = stepOrder[index]

    if (next) setStep(next)
  }

  const progressSteps: ProgressStep[] = $derived([
    {
      id: "confirm",
      label: "Termine bestätigen",
      workflowState: workflowStateForStep({ step: "confirm", currentStep, completedSteps }),
      snippet: confirmStep,
    },
    {
      id: "mail",
      label: "Bestätigungsmail senden",
      workflowState: workflowStateForStep({ step: "mail", currentStep, completedSteps }),
      snippet: mailStep,
    },
    {
      id: "summary",
      label: "Ergebnis",
      workflowState: workflowStateForStep({ step: "summary", currentStep, completedSteps }),
      snippet: summaryStep,
    },
  ])
</script>

{#snippet confirmStep(ctx: ProgressStepContext)}
  <ConfirmEventsStep
    {init}
    output={outputs.confirm}
    readOnly={ctx.readOnly}
    onEvent={applyWorkflowEvent}
    onError={setStepError}
  />
{/snippet}

{#snippet mailStep(ctx: ProgressStepContext)}
  {#if outputs.confirm}
    <SendConfirmationMailStep
      confirmOutput={outputs.confirm}
      output={outputs.mail}
      readOnly={ctx.readOnly}
      onBack={() => setStep("confirm")}
      onEvent={applyWorkflowEvent}
      onError={setStepError}
    />
  {/if}
{/snippet}

{#snippet summaryStep(ctx: ProgressStepContext)}
  <SummaryStep {outputs} readOnly={ctx.readOnly} {finishing} onEvent={applyWorkflowEvent} />
{/snippet}

<div class="confirm-appointments-overlay form light">
  <ProgressSteps
    steps={progressSteps}
    showControls={false}
    darkMode={true}
    syncUrl={false}
    statusMode="explicit"
    navigationMode="guarded"
    lockedStepMessage={LOCKED_WORKFLOW_STEP_MESSAGE}
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
  />
  {#if stepError}
    <ProgressStepsContent>
      <div class="step-error">{stepError}</div>
    </ProgressStepsContent>
  {/if}
</div>

<style>
  .confirm-appointments-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .step-error {
    border: 1px solid var(--error-100);
    color: var(--error-100);
    padding: 0.75rem;
  }
</style>
